import type React from 'react';
import { useSelector } from 'react-redux';
import Container from 'app/shared/core/Container';
import Text from 'app/shared/core/Text';
import {
  listingUtils_bathSqFtRange,
  listingUtils_bedRange,
  listingUtils_summaryPriceText,
} from 'app/shared/utils/listingUtils';
import {
  StyledDetailedRow,
  StyledDetailItem,
  StyledDetailLabel,
  StyledDetailValue,
  StyledPriceRow,
} from './HdpSummary.styles';
import TotalMonthlyPriceBadge from 'app/shared/modules/TotalMonthlyPrice/TotalMonthlyPriceBadge';
import { shouldShowTotalMonthlyPriceBadge } from 'app/shared/utils/totalMonthlyPriceUtils';
import type { ListingDetails } from 'app/types/listingDetails.type';
import type { RootReduxState } from 'app/shared/flux/reducers';

interface HdpSummaryDetailsProps {
  listing: ListingDetails;
}

const HdpSummaryDetails: React.FC<HdpSummaryDetailsProps> = ({ listing }) => {
  const { details, listingMinMaxPriceBeds, pricing, listingType } = listing;
  const summary = pricing?.summary || {};
  const { bedsLow = 0, bedsHigh = 0, bathsLow = 0, bathsHigh = 0, sqFtLow = 0, sqFtHigh = 0 } = summary;
  const {
    minBeds = 0,
    minBaths = 0,
    minSqft = 0,
    maxBeds = 0,
    maxBaths = 0,
    maxSqft = 0,
  } = listingMinMaxPriceBeds || {};
  const { roomSqFt } = details?.roomForRent?.roomDetails || {};
  const { roommatesCount } = details?.roomForRent?.roommateDetails || {};
  const screenWidth = useSelector((state: RootReduxState) => state.app.device.screenWidth);
  const priceText = listingUtils_summaryPriceText(summary);
  const bedText = listingUtils_bedRange(bedsLow || minBeds, bedsHigh || maxBeds);
  const bathText = listingUtils_bathSqFtRange(bathsLow || minBaths, bathsHigh || maxBaths);
  const sqftText = listingUtils_bathSqFtRange(sqFtLow || minSqft, sqFtHigh || maxSqft);
  const showSqft = !sqftText.toString()?.includes('-') && (!roomSqFt || screenWidth === 'xl');
  let roommatesCountLabel = '';
  if (roommatesCount) {
    const isSingular = Number(roommatesCount) === 1;

    if (screenWidth === 'xl') {
      roommatesCountLabel = isSingular ? 'Housemate' : 'Housemates';
    } else {
      roommatesCountLabel = isSingular ? 'House mate' : 'House mates';
    }
  }

  // Helper function for pluralization
  const getPluralizedLabel = (count: number | string, singular: string, plural: string): string => {
    const numCount = typeof count === 'string' ? parseFloat(count) : count;
    return numCount === 1 ? singular : plural;
  };

  // Helper functions to get labels with proper pluralization
  const getBedsLabel = (bedText: string | number | null | undefined): string => {
    if (!bedText) return '';
    const bedStr = String(bedText);
    const count = bedStr.includes('-') ? parseFloat(bedStr.split('-')[1]) : parseFloat(bedStr);
    return getPluralizedLabel(count, 'Bed', 'Beds');
  };

  const getBathsLabel = (bathText: string | number | null | undefined): string => {
    if (!bathText) return '';
    const bathStr = String(bathText);
    const count = bathStr.includes('-') ? parseFloat(bathStr.split('-')[1]) : parseFloat(bathStr);
    return getPluralizedLabel(count, 'Bath', 'Baths');
  };

  return (
    <div className="HdpSummaryDetails">
      <Container>
        {/* First Row: Price + Badge */}
        <StyledPriceRow>
          <StyledDetailValue>
            <Text responsive={{ smAndUp: 'md', mdAndUp: 'lg' }}>{priceText}</Text>
          </StyledDetailValue>
          {shouldShowTotalMonthlyPriceBadge(listing) && (
            <TotalMonthlyPriceBadge listing={listing} size="md" iconVariant="default" />
          )}
        </StyledPriceRow>

        {/* Second Row: Beds, Baths, Sqft with separate values and labels */}
        <StyledDetailedRow isRoomForRent={listingType === 'room'}>
          {bedText && (
            <StyledDetailItem>
              <StyledDetailValue>
                <Text responsive={{ smAndUp: 'md', mdAndUp: 'lg' }}>{bedText}</Text>
              </StyledDetailValue>
              <StyledDetailLabel>
                <Text responsive={{ smAndUp: 'md', mdAndUp: 'lg' }}>{getBedsLabel(bedText)}</Text>
              </StyledDetailLabel>
            </StyledDetailItem>
          )}
          {bathText && (
            <StyledDetailItem>
              <StyledDetailValue>
                <Text responsive={{ smAndUp: 'md', mdAndUp: 'lg' }}>{bathText}</Text>
              </StyledDetailValue>
              <StyledDetailLabel>
                <Text responsive={{ smAndUp: 'md', mdAndUp: 'lg' }}>{getBathsLabel(bathText)}</Text>
              </StyledDetailLabel>
            </StyledDetailItem>
          )}
          {(Boolean(minSqft) || Boolean(maxSqft)) && showSqft && (
            <StyledDetailItem>
              <StyledDetailValue>
                <Text responsive={{ smAndUp: 'md', mdAndUp: 'lg' }}>{sqftText}</Text>
              </StyledDetailValue>
              <StyledDetailLabel>
                <Text responsive={{ smAndUp: 'md', mdAndUp: 'lg' }}>Sqft</Text>
              </StyledDetailLabel>
            </StyledDetailItem>
          )}
          {Boolean(roomSqFt) && (
            <StyledDetailItem>
              <StyledDetailValue>
                <Text responsive={{ smAndUp: 'md', mdAndUp: 'lg' }}>{roomSqFt}</Text>
              </StyledDetailValue>
              <StyledDetailLabel>
                <Text responsive={{ smAndUp: 'md', mdAndUp: 'lg' }}>Sqft room</Text>
              </StyledDetailLabel>
            </StyledDetailItem>
          )}
          {roommatesCount && (
            <StyledDetailItem>
              <StyledDetailValue>
                <Text responsive={{ smAndUp: 'md', mdAndUp: 'lg' }}>{roommatesCount}</Text>
              </StyledDetailValue>
              <StyledDetailLabel>
                <Text responsive={{ smAndUp: 'md', mdAndUp: 'lg' }}>{roommatesCountLabel}</Text>
              </StyledDetailLabel>
            </StyledDetailItem>
          )}
        </StyledDetailedRow>
      </Container>
    </div>
  );
};

export default HdpSummaryDetails;
